import { faInfoCircle } from '@fortawesome/pro-solid-svg-icons';
import { formatPrice } from '../../collection/mappers';
import {
  DiscountDetailsModel,
  DiscountUser,
} from '../model/DiscountDetails.model';
import { Discount } from '../model/Discounts.model';
import { ListRow } from '../view/components/GridList';
import {
  row,
  text,
  title,
  tooltip,
  typography,
  icon,
} from '../view/components/ListCell';
import { DiscountDetailsVM } from './discountDetails.viewModel';
import {
  capitalizeFirstLetter,
  toPeriodLabel,
  toFreeTrialState,
} from './discounts.mappers';
import { bgColorsVars } from '@design-system/vars.css';
import { filterCollection, pipe } from '@client/new/helpers/fns-utils';
import { formatDateOnly } from './utils';
const headLine = (content: string) =>
  typography({
    content,
    variant: 'headline',
    size: 'large',
    fontWeight: 'bold',
  });

const titleWithTooltipIcon = (name: string, content: string) =>
  row(
    [
      title(name),
      tooltip(
        content,
        icon({ src: faInfoCircle, color: bgColorsVars['N-60'] }),
      ),
    ],
    {
      spreadEvenly: true,
    },
  );

export const toPerformanceCards = (
  m: DiscountDetailsModel,
): DiscountDetailsVM['performanceCards'] => {
  const p = m.performance;
  const discount = m.discount;
  const redemptionsStat: ListRow = {
    id: 'redemptions',
    tooltip:
      'The total number of redemptions for this discount, not inclusive of disputes or refunds.',
    cells: [
      titleWithTooltipIcon(
        'Total redemptions',
        'The total number of redemptions for this discount, not inclusive of disputes or refunds.',
      ),
      headLine(p.totalRedemptions + ''),
    ],
  };

  const priceStat: ListRow = {
    id: 'price',
    tooltip: 'Discounted price paid by the user',
    cells: [
      titleWithTooltipIcon(
        'Discounted price',
        'The price the user paid after applying the discount',
      ),
      headLine(formatPrice(p.discountedPrice)),
    ],
  };
  const stats = [redemptionsStat];
  if (
    discount.product.type === 'subscription' &&
    discount.product.value.periods.includes('annual')
  )
    stats.push(toConversionRow('ANNUALLY', p));

  if (discount.product.type === 'otp') stats.push(toConversionRow('OTP', p));

  return [...stats, priceStat];
};

const toConversionRow = (
  type: 'OTP' | 'ANNUALLY',
  p: DiscountDetailsModel['performance'],
): ListRow => {
  const n = type === 'ANNUALLY' ? 'Total upgrades' : 'Total subscribers';
  const t =
    type === 'ANNUALLY'
      ? 'Percentage of users that upgrade from monthly to an annual subscription using the discount'
      : 'Number of purchasers that were also subscribers at the time the discount was redeemed';
  return {
    id: type.toLowerCase() + 'Conversion',
    cells: [
      titleWithTooltipIcon(n, t),
      row([
        headLine(p.totalConversion + ''),
        text(`(${p.totalConversionPercent}%)`),
      ]),
    ],
  };
};

const getResolvedTimeZone = () =>
  Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';

const formatDate = (dateString: string, timeZone: string): string => {
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return '';
  return formatDateOnly(date, '', timeZone);
};

export const toUserRows = (
  users: DiscountDetailsModel['users'],
  timeZone = getResolvedTimeZone(),
): ListRow[] =>
  users
    .sort((a, b) => b.purchaseDate.localeCompare(a.purchaseDate))
    .map((u, i) => ({
      id: u.email + i,
      cells: [
        title(formatDate(u.purchaseDate, timeZone)),
        title(u.name),
        title(u.email),
        title(u.userType),
      ],
    }));

export const toStatusTitle = (p: Discount['product']) =>
  p.type === 'otp-bundle'
    ? `OTP Offer • ${capitalizeFirstLetter(p.value.name || 'N/A')}`
    : p.type === 'otp'
    ? `OTP • ${capitalizeFirstLetter(p.value.name || 'N/A')}`
    : `Subscription • ${toPeriodLabel(p.value.periods)}`;

export const toFreeTrialTitle = (d: Discount): string | null =>
  toFreeTrialState(d) === 'ON' ? '7 day free trial' : null;

export const toBackURL = () =>
  window.location.pathname.replace(/\/[^/]+$/, '').replace(/^\/prev-[^/]+/, '');

export const filterByType =
  (type: DiscountDetailsVM['filters']['type']) =>
  (users: DiscountDetailsModel['users']) => {
    if (!type || type === 'All') return users;
    return users.filter((user) => user?.userType === type);
  };

export const filterUsers = (
  users: DiscountDetailsModel['users'],
  search: string,
  filters: DiscountDetailsVM['filters'],
) => {
  return pipe(
    filterCollection<DiscountUser>(['name', 'userType', 'email'])(search),
    filterByType(filters.type),
  )(users);
};
